<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import DialerAPI from 'dashboard/api/campaigns/dialer';
import { useAlert } from 'dashboard/composables';
import { standardDateTime } from 'shared/helpers/timeHelper';

import Button from 'dashboard/components-next/button/Button.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';

// Everything the dialer wrote down while the campaign ran, read back: what the calls added up to,
// how long each step took, how each agent spent the time, what went wrong, and the steps themselves.
const props = defineProps({
  campaignId: { type: [String, Number], required: true },
});

const { t } = useI18n();

// Must match the controller's page size, or the footer counts pages the server does not have.
const PAGE_SIZE = 50;
const PERIODS = ['today', '7d', '30d'];
const KINDS = [
  'dialed',
  'dial_failed',
  'canceled',
  'overcommitted',
  'answered',
  'machine',
  'bridged',
  'bridge_failed',
  'abandoned',
  'ended',
  'reconciled',
  'orphaned',
  'session_changed',
  'leg_parked',
];
// The steps that are something going wrong, painted so they stand out in the timeline.
const PROBLEM_KINDS = [
  'dial_failed',
  'overcommitted',
  'bridge_failed',
  'abandoned',
  'reconciled',
  'orphaned',
];

const period = ref('today');
const metrics = ref(null);
const isLoading = ref(false);
const kind = ref('');
const taskId = ref(null);
const page = ref(1);
const events = ref([]);
const eventsCount = ref(0);

const kindLabel = value =>
  t(`CAMPAIGN.VOICE.DETAIL.DIAGNOSTICS.KINDS.${value.toUpperCase()}`);
const stateLabel = value =>
  value
    ? t(`CAMPAIGN.VOICE.DETAIL.DIAGNOSTICS.STATES.${value.toUpperCase()}`)
    : '—';
const causeLabel = value =>
  t(`CAMPAIGN.VOICE.DETAIL.DIAGNOSTICS.CAUSES.${value.toUpperCase()}`);

const kindOptions = computed(() => [
  {
    value: '',
    label: t('CAMPAIGN.VOICE.DETAIL.DIAGNOSTICS.TIMELINE.ALL_KINDS'),
  },
  ...KINDS.map(value => ({ value, label: kindLabel(value) })),
]);

const formatSeconds = value => {
  if (value === null || value === undefined) return '—';
  const total = Math.round(value);
  if (total < 60) return `${total}s`;
  const minutes = Math.floor(total / 60);
  if (minutes < 60) return `${minutes}m ${total % 60}s`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
};
const formatMs = value =>
  value === null || value === undefined ? '—' : `${Math.round(value)} ms`;
const formatTime = iso =>
  standardDateTime(Math.floor(new Date(iso).getTime() / 1000));

const callTiles = computed(() => {
  const calls = metrics.value?.calls || {};
  return [
    { key: 'dialed', value: calls.dialed },
    { key: 'answered', value: calls.answered },
    { key: 'bridged', value: calls.bridged },
    {
      key: 'abandoned',
      value: calls.abandoned,
      hint: t('CAMPAIGN.VOICE.DETAIL.DIAGNOSTICS.CALLS.ABANDON_RATE', {
        rate: calls.abandon_rate ?? 0,
      }),
      alert: calls.abandoned > 0,
    },
    { key: 'canceled', value: calls.canceled },
    { key: 'overcommitted', value: calls.overcommitted },
    { key: 'machine', value: calls.machine },
    {
      key: 'dial_failed',
      value: calls.dial_failed,
      alert: calls.dial_failed > 0,
    },
  ].map(tile => ({
    ...tile,
    label: t(
      `CAMPAIGN.VOICE.DETAIL.DIAGNOSTICS.CALLS.${tile.key.toUpperCase()}`
    ),
  }));
});

const timingRows = computed(() => {
  const timings = metrics.value?.timings || {};
  return [
    'ring_seconds',
    'bridge_ms',
    'talk_seconds',
    'idle_seconds',
    'wrap_up_seconds',
  ].map(key => {
    const format = key === 'bridge_ms' ? formatMs : formatSeconds;
    const row = timings[key] || {};
    return {
      key,
      label: t(
        `CAMPAIGN.VOICE.DETAIL.DIAGNOSTICS.TIMINGS.${key.toUpperCase()}`
      ),
      p50: format(row.p50),
      p90: format(row.p90),
      max: format(row.max),
      count: row.count ?? 0,
    };
  });
});

const causes = computed(() =>
  Object.entries(metrics.value?.abandoned_causes || {}).map(
    ([cause, count]) => ({ cause, count, label: causeLabel(cause) })
  )
);
const agents = computed(() => metrics.value?.agents || []);
const errors = computed(() => metrics.value?.errors || []);

// One line on what a step means, from what it carried. The raw data stays one click away in the API.
const eventDetail = event => {
  const data = event.data || {};
  switch (event.kind) {
    case 'dialed':
      return t('CAMPAIGN.VOICE.DETAIL.DIAGNOSTICS.DETAILS.DIALED', {
        attempt: data.attempt,
        ready: data.ready_agents ?? '—',
        ringing: data.ringing ?? '—',
      });
    case 'answered':
      return t('CAMPAIGN.VOICE.DETAIL.DIAGNOSTICS.DETAILS.ANSWERED', {
        time: formatSeconds(data.ring_seconds),
      });
    case 'bridged':
      return t('CAMPAIGN.VOICE.DETAIL.DIAGNOSTICS.DETAILS.BRIDGED', {
        time: formatMs(data.bridge_ms),
      });
    case 'ended':
      return t('CAMPAIGN.VOICE.DETAIL.DIAGNOSTICS.DETAILS.ENDED', {
        status: data.provider_status,
        time: formatSeconds(data.talk_seconds),
      });
    case 'session_changed':
      return t('CAMPAIGN.VOICE.DETAIL.DIAGNOSTICS.DETAILS.SESSION_CHANGED', {
        from: stateLabel(data.from),
        to: stateLabel(data.to),
        time: formatSeconds(data.seconds),
      });
    case 'leg_parked':
      return data.repark
        ? t('CAMPAIGN.VOICE.DETAIL.DIAGNOSTICS.DETAILS.LEG_PARKED_REPARK')
        : t('CAMPAIGN.VOICE.DETAIL.DIAGNOSTICS.DETAILS.LEG_PARKED');
    case 'overcommitted':
      return t('CAMPAIGN.VOICE.DETAIL.DIAGNOSTICS.DETAILS.OVERCOMMITTED', {
        ringing: data.ringing,
        ready: data.ready_agents,
      });
    case 'abandoned':
      return causeLabel(data.cause);
    case 'machine':
      return data.reached_agent
        ? t('CAMPAIGN.VOICE.DETAIL.DIAGNOSTICS.DETAILS.MACHINE_ON_AGENT')
        : '';
    default:
      return [data.code, data.message].filter(Boolean).join(' · ');
  }
};

const fetchMetrics = async () => {
  isLoading.value = true;
  try {
    const { data } = await DialerAPI.metrics(props.campaignId, period.value);
    metrics.value = data;
  } catch (error) {
    useAlert(t('CAMPAIGN.VOICE.DETAIL.DIAGNOSTICS.LOAD_FAILED'));
  } finally {
    isLoading.value = false;
  }
};

const fetchEvents = async () => {
  try {
    const { data } = await DialerAPI.events(props.campaignId, {
      page: page.value,
      kind: kind.value || undefined,
      taskId: taskId.value || undefined,
    });
    events.value = data.payload;
    eventsCount.value = data.meta.count;
  } catch (error) {
    useAlert(t('CAMPAIGN.VOICE.DETAIL.DIAGNOSTICS.LOAD_FAILED'));
  }
};

const refresh = () => Promise.all([fetchMetrics(), fetchEvents()]);

const applyFilters = () => {
  if (page.value === 1) fetchEvents();
  else page.value = 1;
};

const showCall = event => {
  kind.value = '';
  taskId.value = event.task_id;
  applyFilters();
};

const showAllCalls = () => {
  taskId.value = null;
  applyFilters();
};

watch(period, fetchMetrics);
watch(kind, applyFilters);
watch(page, fetchEvents);
onMounted(refresh);
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex flex-wrap items-center justify-between gap-3">
      <div class="flex flex-col gap-1">
        <span class="text-base font-medium text-n-slate-12">
          {{ t('CAMPAIGN.VOICE.DETAIL.DIAGNOSTICS.TITLE') }}
        </span>
        <span class="text-sm text-n-slate-11">
          {{ t('CAMPAIGN.VOICE.DETAIL.DIAGNOSTICS.DESCRIPTION') }}
        </span>
      </div>
      <div class="flex items-center gap-2">
        <Button
          v-for="value in PERIODS"
          :key="value"
          :label="
            t(`CAMPAIGN.VOICE.DETAIL.DIAGNOSTICS.PERIOD.${value.toUpperCase()}`)
          "
          :variant="period === value ? 'solid' : 'faded'"
          :color="period === value ? 'blue' : 'slate'"
          size="sm"
          @click="period = value"
        />
        <Button
          icon="i-lucide-refresh-cw"
          variant="ghost"
          color="slate"
          size="sm"
          :is-loading="isLoading"
          :aria-label="t('CAMPAIGN.VOICE.DETAIL.DIAGNOSTICS.REFRESH')"
          @click="refresh"
        />
      </div>
    </div>

    <div class="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <div
        v-for="tile in callTiles"
        :key="tile.key"
        class="flex flex-col gap-1 p-4 border rounded-xl bg-n-solid-1"
        :class="tile.alert ? 'border-n-ruby-6' : 'border-n-weak'"
      >
        <span class="text-sm text-n-slate-11">{{ tile.label }}</span>
        <span
          class="text-xl font-medium"
          :class="tile.alert ? 'text-n-ruby-11' : 'text-n-slate-12'"
        >
          {{ tile.value ?? 0 }}
        </span>
        <span v-if="tile.hint" class="text-xs text-n-slate-11">
          {{ tile.hint }}
        </span>
      </div>
    </div>

    <div
      v-if="causes.length"
      class="flex flex-col gap-2 p-4 border rounded-xl border-n-ruby-6 bg-n-ruby-2"
    >
      <span class="text-sm font-medium text-n-ruby-11">
        {{ t('CAMPAIGN.VOICE.DETAIL.DIAGNOSTICS.CAUSES.TITLE') }}
      </span>
      <div
        v-for="item in causes"
        :key="item.cause"
        class="flex items-center justify-between gap-3 text-sm text-n-ruby-11"
      >
        <span>{{ item.label }}</span>
        <span class="font-medium tabular-nums">{{ item.count }}</span>
      </div>
    </div>

    <div class="flex flex-col gap-2">
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.VOICE.DETAIL.DIAGNOSTICS.TIMINGS.TITLE') }}
      </span>
      <div class="overflow-x-auto border rounded-xl border-n-weak">
        <table class="w-full text-sm">
          <thead>
            <tr class="bg-n-alpha-1 text-n-slate-11">
              <th class="px-4 py-2 font-medium text-left">
                {{ t('CAMPAIGN.VOICE.DETAIL.DIAGNOSTICS.TIMINGS.STEP') }}
              </th>
              <th class="px-4 py-2 font-medium text-right">
                {{ t('CAMPAIGN.VOICE.DETAIL.DIAGNOSTICS.TIMINGS.MEDIAN') }}
              </th>
              <th class="px-4 py-2 font-medium text-right">
                {{ t('CAMPAIGN.VOICE.DETAIL.DIAGNOSTICS.TIMINGS.P90') }}
              </th>
              <th class="px-4 py-2 font-medium text-right">
                {{ t('CAMPAIGN.VOICE.DETAIL.DIAGNOSTICS.TIMINGS.MAX') }}
              </th>
              <th class="px-4 py-2 font-medium text-right">
                {{ t('CAMPAIGN.VOICE.DETAIL.DIAGNOSTICS.TIMINGS.COUNT') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in timingRows"
              :key="row.key"
              class="border-t border-n-weak"
            >
              <td class="px-4 py-2 text-n-slate-12">{{ row.label }}</td>
              <td class="px-4 py-2 text-right tabular-nums text-n-slate-12">
                {{ row.p50 }}
              </td>
              <td class="px-4 py-2 text-right tabular-nums text-n-slate-11">
                {{ row.p90 }}
              </td>
              <td class="px-4 py-2 text-right tabular-nums text-n-slate-11">
                {{ row.max }}
              </td>
              <td class="px-4 py-2 text-right tabular-nums text-n-slate-11">
                {{ row.count }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="flex flex-col gap-2">
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.VOICE.DETAIL.DIAGNOSTICS.AGENTS.TITLE') }}
      </span>
      <div
        v-if="agents.length"
        class="overflow-x-auto border rounded-xl border-n-weak"
      >
        <table class="w-full text-sm">
          <thead>
            <tr class="bg-n-alpha-1 text-n-slate-11">
              <th class="px-4 py-2 font-medium text-left">
                {{ t('CAMPAIGN.VOICE.DETAIL.DIAGNOSTICS.AGENTS.NAME') }}
              </th>
              <th class="px-4 py-2 font-medium text-right">
                {{ t('CAMPAIGN.VOICE.DETAIL.DIAGNOSTICS.AGENTS.CALLS') }}
              </th>
              <th class="px-4 py-2 font-medium text-right">
                {{ t('CAMPAIGN.VOICE.DETAIL.DIAGNOSTICS.AGENTS.TALK') }}
              </th>
              <th class="px-4 py-2 font-medium text-right">
                {{ t('CAMPAIGN.VOICE.DETAIL.DIAGNOSTICS.AGENTS.FREE') }}
              </th>
              <th class="px-4 py-2 font-medium text-right">
                {{ t('CAMPAIGN.VOICE.DETAIL.DIAGNOSTICS.AGENTS.WRAP_UP') }}
              </th>
              <th class="px-4 py-2 font-medium text-right">
                {{ t('CAMPAIGN.VOICE.DETAIL.DIAGNOSTICS.AGENTS.AWAY') }}
              </th>
              <th class="px-4 py-2 font-medium text-right">
                {{ t('CAMPAIGN.VOICE.DETAIL.DIAGNOSTICS.AGENTS.OCCUPANCY') }}
              </th>
              <th class="px-4 py-2 font-medium text-right">
                {{ t('CAMPAIGN.VOICE.DETAIL.DIAGNOSTICS.AGENTS.LEG_FAILURES') }}
              </th>
              <th class="px-4 py-2 font-medium text-right">
                {{ t('CAMPAIGN.VOICE.DETAIL.DIAGNOSTICS.AGENTS.REPARKS') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="agent in agents"
              :key="agent.user_id"
              class="border-t border-n-weak"
            >
              <td class="px-4 py-2 truncate text-n-slate-12">
                {{ agent.name }}
              </td>
              <td class="px-4 py-2 text-right tabular-nums text-n-slate-12">
                {{ agent.calls }}
              </td>
              <td class="px-4 py-2 text-right tabular-nums text-n-slate-11">
                {{ formatSeconds(agent.talk_seconds) }}
              </td>
              <td class="px-4 py-2 text-right tabular-nums text-n-slate-11">
                {{ formatSeconds(agent.free_seconds) }}
              </td>
              <td class="px-4 py-2 text-right tabular-nums text-n-slate-11">
                {{ formatSeconds(agent.wrap_up_seconds) }}
              </td>
              <td class="px-4 py-2 text-right tabular-nums text-n-slate-11">
                {{ formatSeconds(agent.away_seconds) }}
              </td>
              <td class="px-4 py-2 text-right tabular-nums text-n-slate-11">
                {{ `${agent.occupancy}%` }}
              </td>
              <td
                class="px-4 py-2 text-right tabular-nums"
                :class="
                  agent.leg_failures ? 'text-n-ruby-11' : 'text-n-slate-11'
                "
              >
                {{ agent.leg_failures }}
              </td>
              <td
                class="px-4 py-2 text-right tabular-nums"
                :class="agent.reparks ? 'text-n-amber-11' : 'text-n-slate-11'"
              >
                {{ agent.reparks }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <span v-else class="text-sm text-n-slate-11">
        {{ t('CAMPAIGN.VOICE.DETAIL.DIAGNOSTICS.AGENTS.EMPTY') }}
      </span>
    </div>

    <div class="flex flex-col gap-2">
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.VOICE.DETAIL.DIAGNOSTICS.ERRORS.TITLE') }}
      </span>
      <div
        v-if="errors.length"
        class="overflow-x-auto border rounded-xl border-n-weak"
      >
        <table class="w-full text-sm">
          <thead>
            <tr class="bg-n-alpha-1 text-n-slate-11">
              <th class="px-4 py-2 font-medium text-left">
                {{ t('CAMPAIGN.VOICE.DETAIL.DIAGNOSTICS.ERRORS.KIND') }}
              </th>
              <th class="px-4 py-2 font-medium text-left">
                {{ t('CAMPAIGN.VOICE.DETAIL.DIAGNOSTICS.ERRORS.CODE') }}
              </th>
              <th class="px-4 py-2 font-medium text-right">
                {{ t('CAMPAIGN.VOICE.DETAIL.DIAGNOSTICS.ERRORS.COUNT') }}
              </th>
              <th class="px-4 py-2 font-medium text-left">
                {{ t('CAMPAIGN.VOICE.DETAIL.DIAGNOSTICS.ERRORS.LAST') }}
              </th>
              <th class="px-4 py-2 font-medium text-left">
                {{ t('CAMPAIGN.VOICE.DETAIL.DIAGNOSTICS.ERRORS.MESSAGE') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="error in errors"
              :key="`${error.kind}-${error.code}`"
              class="border-t border-n-weak"
            >
              <td class="px-4 py-2 text-n-slate-12">
                {{ kindLabel(error.kind) }}
              </td>
              <td class="px-4 py-2 tabular-nums text-n-slate-11">
                {{ error.code || '—' }}
              </td>
              <td class="px-4 py-2 text-right tabular-nums text-n-ruby-11">
                {{ error.count }}
              </td>
              <td class="px-4 py-2 tabular-nums text-n-slate-11">
                {{ formatTime(error.last_at) }}
              </td>
              <td class="px-4 py-2 truncate text-n-slate-11">
                {{ error.message || '—' }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <span v-else class="text-sm text-n-slate-11">
        {{ t('CAMPAIGN.VOICE.DETAIL.DIAGNOSTICS.ERRORS.EMPTY') }}
      </span>
    </div>

    <div class="flex flex-col gap-2">
      <div class="flex flex-wrap items-center justify-between gap-2">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.VOICE.DETAIL.DIAGNOSTICS.TIMELINE.TITLE') }}
        </span>
        <div class="flex items-center gap-2">
          <Button
            v-if="taskId"
            :label="t('CAMPAIGN.VOICE.DETAIL.DIAGNOSTICS.TIMELINE.ALL_CALLS')"
            icon="i-lucide-x"
            variant="faded"
            color="slate"
            size="sm"
            @click="showAllCalls"
          />
          <Select
            v-model="kind"
            :options="kindOptions"
            :aria-label="t('CAMPAIGN.VOICE.DETAIL.DIAGNOSTICS.TIMELINE.KIND')"
          />
        </div>
      </div>
      <div
        v-if="events.length"
        class="overflow-x-auto border rounded-xl border-n-weak"
      >
        <table class="w-full text-sm">
          <tbody>
            <tr
              v-for="event in events"
              :key="event.id"
              class="border-t first:border-t-0 border-n-weak"
            >
              <td
                class="px-4 py-2 whitespace-nowrap tabular-nums text-n-slate-11"
              >
                {{ formatTime(event.created_at) }}
              </td>
              <td
                class="px-4 py-2 font-medium whitespace-nowrap"
                :class="
                  PROBLEM_KINDS.includes(event.kind)
                    ? 'text-n-ruby-11'
                    : 'text-n-slate-12'
                "
              >
                {{ kindLabel(event.kind) }}
              </td>
              <td class="px-4 py-2 text-n-slate-11">
                {{ event.contact_name || event.agent || '—' }}
                <span v-if="event.phone_number" class="text-n-slate-10">
                  {{ event.phone_number }}
                </span>
              </td>
              <td class="px-4 py-2 text-n-slate-11">
                {{ eventDetail(event) }}
              </td>
              <td class="px-2 py-2 text-right">
                <Button
                  v-if="event.task_id && !taskId"
                  :label="
                    t('CAMPAIGN.VOICE.DETAIL.DIAGNOSTICS.TIMELINE.THIS_CALL')
                  "
                  variant="ghost"
                  color="slate"
                  size="xs"
                  @click="showCall(event)"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <span v-else class="text-sm text-n-slate-11">
        {{ t('CAMPAIGN.VOICE.DETAIL.DIAGNOSTICS.TIMELINE.EMPTY') }}
      </span>
      <PaginationFooter
        v-if="eventsCount > PAGE_SIZE"
        v-model:current-page="page"
        :total-items="eventsCount"
        :items-per-page="PAGE_SIZE"
        current-page-info="CAMPAIGN.VOICE.DETAIL.DIAGNOSTICS.TIMELINE.PAGINATION"
      />
    </div>
  </div>
</template>
